/**
 * Message search (phase-1 "simple search"; docs/design/visual-overhaul.md
 * §6.5, §7.26). The query is typed in the channel header's search field
 * (HeaderSearch); the results dock in the member-list slot (SearchPanel),
 * grouped by channel, with a 48px header holding the title, the count and
 * Close. Filters from:, in:, has: are resolved against the people and
 * channels READY sent; a filter that matches nothing is shown, never
 * dropped. Results come only from `GET /guilds/{id}/messages/search`, which
 * returns only channels you can read. A result opens its message (loading
 * older history if needed).
 */
import { isVoiceChannelType, type Message, type Snowflake } from '@neosocial/protocol';
import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { useData } from '../store/data';
import { userName } from '../store/selectors';
import { useUi } from '../store/ui';
import { openChannelById } from '../threads/entry';
import { Avatar, Icon, IconBtn } from '../ui/kit';
import { toast } from '../ui/overlay';
import { renderMarkdown } from './markdown';
import { useResolver } from './ChannelView';
import { parseSearch, resolveSearch } from './searchQuery';

const PAGE = 25;

type Result =
  | { st: 'done'; key: string; total: number; messages: Message[]; offset: number }
  | { st: 'error'; key: string };
type State = { st: 'idle' } | { st: 'loading'; key: string } | Result;

/**
 * One row of a search result or a pin (§7.26): author, place and time, the
 * text, and a Jump chip at the end. The row is not a button: its text holds
 * mentions and links that are buttons themselves (a button can't hold a
 * button), and a click on one does only that (markdown.tsx stops it there).
 * A click anywhere else, or the Jump chip, opens the message.
 */
export function PreviewRow({
  m,
  onOpen,
  close,
  place,
  cls,
}: {
  m: Message;
  onOpen(): void;
  close(): void;
  place?: ReactNode;
  cls?: string;
}) {
  const { t, fmt } = useT();
  const channel = useData((d) => d.channels[m.channel_id]);
  const gid = channel?.guild_id ?? null;
  const name = useData((d) => userName(d, m.author.id, gid));
  const author = useData((d) => d.users[m.author.id] ?? m.author);
  const base = useResolver(channel);
  const resolver = useMemo(
    () => ({
      ...base,
      mentionEveryone: m.mention_everyone,
      onChannel: (id: Snowflake) => {
        close();
        base.onChannel?.(id);
      },
    }),
    [base, close, m.mention_everyone],
  );
  return (
    <li className={'ns-sr' + (cls ? ' ' + cls : '')} onClick={onOpen}>
      <Avatar user={author} name={name} size={32} />
      <span className="ns-sr-b">
        <span className="ns-sr-h">
          <b dir="auto">{name}</b>
          {place}
          <time dateTime={m.timestamp} data-tip={fmt.date(m.timestamp)}>
            {fmt.stamp(m.timestamp)}
          </time>
        </span>
        <span className="ns-sr-t" dir="auto">
          {m.content
            ? renderMarkdown(m.content, resolver, t('hub.spoiler'), { preview: true })
            : null}
          {m.attachments.length ? (
            <span className="ns-muted">
              {' '}
              <Icon name="image" size="s" /> {fmt.num(m.attachments.length)}
            </span>
          ) : null}
        </span>
      </span>
      <button
        type="button"
        className="ns-sr-jump"
        onClick={(e) => {
          e.stopPropagation();
          onOpen();
        }}
      >
        {t('chat.jump')}
      </button>
    </li>
  );
}

function ResultRow({ m, onOpen, close }: { m: Message; onOpen(): void; close(): void }) {
  const { t } = useT();
  const channel = useData((d) => d.channels[m.channel_id]);
  return (
    <PreviewRow
      m={m}
      onOpen={onOpen}
      close={close}
      place={
        <span className="ns-muted">
          {t(
            // a voice or stage channel's own chat: no '#'
            channel && isVoiceChannelType(channel.type)
              ? 'um.search.in.voice'
              : 'um.search.in.channel',
            { channel: channel?.name ?? '' },
          )}
        </span>
      }
    />
  );
}

/** The filter chips: they add a filter to the query. */
function FilterChips({ insert }: { insert(token: string): void }) {
  const { t } = useT();
  const keep = (e: { preventDefault(): void }) => e.preventDefault();
  return (
    <div className="ns-chips-row ns-search-chips">
      <button
        type="button"
        className="nx-chipbtn"
        onPointerDown={keep}
        onClick={() => insert('from:')}
      >
        {t('um.search.from')}
      </button>
      <button
        type="button"
        className="nx-chipbtn"
        onPointerDown={keep}
        onClick={() => insert('in:#')}
      >
        {t('um.search.in')}
      </button>
      <button
        type="button"
        className="nx-chipbtn"
        onPointerDown={keep}
        onClick={() => insert('has:image ')}
      >
        {t('um.search.hasImage')}
      </button>
      <button
        type="button"
        className="nx-chipbtn"
        onPointerDown={keep}
        onClick={() => insert('has:link ')}
      >
        {t('um.search.hasLink')}
      </button>
    </div>
  );
}

const withToken = (v: string, token: string) => (v && !v.endsWith(' ') ? v + ' ' : v) + token;

/**
 * The results, in the member-list slot. With `query` the header's field
 * drives it; without, it has a field of its own.
 */
export function SearchPanel({
  guildId,
  inChannel,
  close,
  query,
  onQuery,
}: {
  guildId: Snowflake;
  inChannel?: string;
  close(): void;
  query?: string;
  onQuery?(v: string): void;
}) {
  const { t, fmt } = useT();
  const controlled = query !== undefined;
  const [own, setOwn] = useState(inChannel ? `in:#${inChannel} ` : '');
  const input = controlled ? query : own;
  const setInput = (v: string) => (controlled ? onQuery?.(v) : setOwn(v));
  const [debounced, setDebounced] = useState(input);
  const [result, setResult] = useState<Result | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const members = useData((d) => d.members[guildId]);
  const channels = useData((d) => d.channels);
  useEffect(() => {
    const h = setTimeout(() => setDebounced(input), 300);
    return () => clearTimeout(h);
  }, [input]);
  const resolved = useMemo(
    () => resolveSearch(useData.getState(), guildId, parseSearch(debounced), PAGE, 0),
    // names resolve against the members and channels READY sent
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debounced, guildId, members, channels],
  );
  const key = resolved.ready ? JSON.stringify(resolved.query) : '';
  // what is on screen follows the current query: idle, loading until its answer, then it
  const state: State = useMemo(
    () => (!key ? { st: 'idle' } : result?.key === key ? result : { st: 'loading', key }),
    [key, result],
  );
  useEffect(() => {
    if (!key) return;
    let live = true;
    api.um
      .search(guildId, resolved.query)
      .then(
        (r) =>
          live &&
          setResult({ st: 'done', key, total: r.total_results, messages: r.messages, offset: 0 }),
      )
      .catch(() => live && setResult({ st: 'error', key }));
    return () => {
      live = false;
    };
    // the key carries the query
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, guildId]);
  const more = () => {
    if (state.st !== 'done') return;
    const next = state.offset + PAGE;
    api.um
      .search(guildId, { ...resolved.query, offset: next })
      .then((r) =>
        setResult((s) =>
          s?.st === 'done' && s.key === key
            ? {
                ...s,
                offset: next,
                total: r.total_results,
                messages: [...s.messages, ...r.messages],
              }
            : s,
        ),
      )
      .catch(() => setResult({ st: 'error', key }));
  };
  const insert = (token: string) => {
    setInput(withToken(input, token));
    if (!controlled) inputRef.current?.focus();
  };
  const open = (m: Message) => {
    if (!controlled) close();
    // a thread the store doesn't hold (archived) is fetched first (threads/)
    openChannelById(m.channel_id, () =>
      useUi.getState().set({ jump: { channelId: m.channel_id, messageId: m.id } }),
    );
  };
  // grouped by channel, in the order the server ranked them
  const groups = useMemo(() => {
    if (state.st !== 'done') return [];
    const out: Array<{ channelId: Snowflake; items: Message[] }> = [];
    for (const m of state.messages) {
      const g = out.find((x) => x.channelId === m.channel_id);
      if (g) g.items.push(m);
      else out.push({ channelId: m.channel_id, items: [m] });
    }
    return out;
  }, [state]);
  return (
    <div className="ns-search slot-panel" role="region" aria-label={t('um.search.label')}>
      <div className="slot-h">
        <Icon name="search" size="m" />
        <h2 className="slot-t">{t('chat.search.title')}</h2>
        {state.st === 'done' ? (
          <span className="slot-n ns-search-count nx-num">
            {t('um.search.results', { n: state.total })}
          </span>
        ) : null}
        <IconBtn icon="x" label={t('c.close')} onClick={close} />
      </div>
      {!controlled ? (
        <label className="ns-search-in">
          <Icon name="search" size="s" />
          <input
            ref={inputRef}
            type="search"
            dir="auto"
            value={input}
            placeholder={t('um.search.ph')}
            aria-label={t('um.search.label')}
            data-autofocus
            onChange={(e) => setInput(e.target.value)}
          />
        </label>
      ) : null}
      <FilterChips insert={insert} />
      {resolved.unknown.map((u) => (
        <p key={u.filter + u.value} className="ns-err slot-note" role="alert">
          {u.filter === 'from'
            ? t('um.search.unknownFrom', { name: u.value })
            : u.filter === 'in'
              ? t('um.search.unknownIn', { name: u.value })
              : t('um.search.unknownHas')}
        </p>
      ))}
      <div className="ns-search-list nx-scroll" aria-live="polite">
        {state.st === 'idle' ? <p className="ns-muted slot-note">{t('um.search.help')}</p> : null}
        {state.st === 'loading' ? (
          <p className="ns-muted slot-note">
            <i className="nx-spin" /> {t('ns.loading')}
          </p>
        ) : null}
        {state.st === 'error' ? <p className="ns-err slot-note">{t('um.search.failed')}</p> : null}
        {state.st === 'done' ? (
          <>
            {!state.messages.length ? (
              <div className="slot-empty">
                <span className="slot-empty-ic" aria-hidden="true">
                  <Icon name="search" size="xl" />
                </span>
                <p>{t('um.search.none')}</p>
              </div>
            ) : null}
            {groups.map((g) => (
              <Fragment key={g.channelId}>
                <ChannelGroupHead channelId={g.channelId} />
                <ul>
                  {g.items.map((m) => (
                    <ResultRow key={m.id} m={m} onOpen={() => open(m)} close={close} />
                  ))}
                </ul>
              </Fragment>
            ))}
            {state.messages.length < state.total ? (
              <button type="button" className="ns-link slot-more" onClick={more}>
                {t('um.search.more')} ({fmt.num(state.total - state.messages.length)})
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

function ChannelGroupHead({ channelId }: { channelId: Snowflake }) {
  const channel = useData((d) => d.channels[channelId]);
  const voice = !!channel && isVoiceChannelType(channel.type);
  return (
    <h3 className="ns-sr-g">
      <Icon name={voice ? 'volume' : 'hash'} size="s" />
      <span dir="auto">{channel?.name ?? ''}</span>
    </h3>
  );
}

/**
 * The header's search field (§6.5): 28 tall, 200 wide, 280 while focused.
 * Typing opens the results in the slot; the filter chips drop down under it
 * while it is focused and empty. Esc clears it and closes the results.
 * Turned off by the admins, it stays in place, disabled, saying why.
 */
export function HeaderSearch({
  value,
  onChange,
  open,
  onClose,
  enabled,
}: {
  value: string;
  onChange(v: string): void;
  open: boolean;
  onClose(): void;
  enabled: boolean;
}) {
  const { t } = useT();
  const ref = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  if (!enabled) {
    const off = t('cfg.off.search');
    return (
      <div className="ch-search is-off">
        <Icon name="search" size="s" />
        <input
          type="search"
          readOnly
          aria-label={off}
          aria-disabled="true"
          placeholder={t('um.search.ph')}
          data-tip={off}
          data-tip-pos="bottom"
          onClick={() => toast(off, { icon: 'search', tone: 'warn' })}
        />
      </div>
    );
  }
  const key = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      onChange('');
      onClose();
      ref.current?.blur();
    }
  };
  return (
    <div className={'ch-search ns-search-in' + (focused || value ? ' is-open' : '')} role="search">
      <Icon name="search" size="s" />
      <input
        ref={ref}
        type="search"
        dir="auto"
        value={value}
        placeholder={t('um.search.ph')}
        aria-label={t('um.search.label')}
        aria-expanded={open}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={key}
      />
      {value ? (
        <button
          type="button"
          className="ch-search-x"
          aria-label={t('chat.search.clear')}
          onPointerDown={(e) => e.preventDefault()}
          onClick={() => {
            onChange('');
            onClose();
          }}
        >
          <Icon name="x" size="s" />
        </button>
      ) : null}
      {focused && !open ? (
        <div className="ch-search-pop nx-raised" role="note">
          <p className="ch-search-help">{t('chat.search.filters')}</p>
          <FilterChips insert={(tok) => onChange(withToken(value, tok))} />
        </div>
      ) : null}
    </div>
  );
}
